// src/contexts/AuthContext.tsx
import React, {
  createContext, useContext, useState, useEffect,
  ReactNode, useCallback, useMemo, useRef,
} from 'react';
import { supabase } from '@/config/supabase';
import { Session, User } from '@supabase/supabase-js';

export type UserRole = 'Guest' | 'Resident' | 'Business' | 'Landlord' | 'Staff' | 'Admin' | 'Tenant' | 'BusinessOwner';

export interface UserProfile {
  id: string;
  full_name: string;
  avatar_url?: string;
  username?: string;
  email?: string;
  phone_number?: string;
  bio?: string;
  roles: UserRole[];
  active_role: UserRole;
  onboarding_completed: boolean;
  business_profile?: any;
  landlord_profile?: any;
  staff_profile?: any;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasAllRoles: (roles: UserRole[]) => boolean;
  isAuthenticated: boolean;
  activeRole: UserRole | null;
  setActiveRole: (role: UserRole) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  canAccessPortal: (portal: 'business' | 'landlord' | 'staff' | 'admin' | 'tenant') => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

// ── Fetch profile from Supabase (no navigate dependency) ──────────────────────
async function fetchProfileFromDB(userId: string, userMeta?: User | null): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id,full_name,avatar_url,username,phone_number,bio,roles,active_role,onboarding_completed')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.warn('Profile fetch error:', error.message);
      return null;
    }

    if (!data) {
      return {
        id: userId,
        full_name: userMeta?.user_metadata?.full_name || userMeta?.email?.split('@')[0] || 'User',
        email: userMeta?.email,
        roles: ['Guest'],
        active_role: 'Guest',
        onboarding_completed: false,
      } as UserProfile;
    }

    let roles: UserRole[] = Array.isArray(data.roles) && data.roles.length > 0
      ? [...data.roles]
      : ['Guest'];

    if (!roles.includes('Guest')) roles.push('Guest');
    if (roles.includes('Landlord') && !roles.includes('Business')) roles.push('Business');

    const active_role: UserRole = data.active_role && roles.includes(data.active_role)
      ? data.active_role
      : roles[0] || 'Guest';

    return { ...data, roles, active_role };
  } catch (err) {
    console.warn('Profile fetch failed:', err);
    return null;
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser]       = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Use a ref for navigate so it never appears in effect deps
  const navigateRef = useRef<((path: string) => void) | null>(null);

  // Inject navigate lazily — called from components that have router context
  // We avoid importing useNavigate here to prevent the effect from re-running
  const setNavigate = useCallback((fn: (path: string) => void) => {
    navigateRef.current = fn;
  }, []);

  // ── One-time auth initialisation ─────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    // Safety: if Supabase hangs, stop loading after 5 seconds
    const safetyTimer = setTimeout(() => {
      if (mounted) {
        console.warn('[AuthContext] getSession timed out — clearing loading');
        setLoading(false);
      }
    }, 5000);

    const init = async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!mounted) return;

        setSession(s);
        setUser(s?.user ?? null);

        if (s?.user) {
          const p = await fetchProfileFromDB(s.user.id, s.user);
          if (mounted) setProfile(p);
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        clearTimeout(safetyTimer);
        if (mounted) setLoading(false);
      }
    };

    init();


    // Listen only for real sign-in / sign-out events — NOT INITIAL_SESSION
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;

      // Skip INITIAL_SESSION — already handled above
      if (event === 'INITIAL_SESSION') return;

      console.log('Auth state changed:', event);

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        const p = await fetchProfileFromDB(newSession.user.id, newSession.user);
        if (!mounted) return;
        setProfile(p);

        // The app routing handles role-based redirects on initial boot natively in App.tsx
        // so we do not force redirects here to prevent user-trapping loops.
      } else {
        setProfile(null);
        if (event === 'SIGNED_OUT' && navigateRef.current) {
          navigateRef.current('/login');
        }
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []); // ← empty deps: runs exactly once

  // ── Role helpers ──────────────────────────────────────────────────────────────
  const hasRole = useCallback((role: UserRole | UserRole[]) => {
    if (!profile?.roles) return false;
    return Array.isArray(role)
      ? role.some(r => profile.roles.includes(r))
      : profile.roles.includes(role);
  }, [profile]);

  const hasAnyRole = useCallback((roles: UserRole[]) =>
    !!profile?.roles && roles.some(r => profile.roles.includes(r)), [profile]);

  const hasAllRoles = useCallback((roles: UserRole[]) =>
    !!profile?.roles && roles.every(r => profile.roles.includes(r)), [profile]);

  const canAccessPortal = useCallback((portal: 'business' | 'landlord' | 'staff' | 'admin' | 'tenant') => {
    if (!profile?.roles) return false;
    switch (portal) {
      case 'business': return profile.roles.includes('Business') || profile.roles.includes('BusinessOwner' as UserRole);
      case 'landlord': return profile.roles.includes('Landlord');
      case 'staff':    return profile.roles.includes('Staff');
      case 'admin':    return profile.roles.includes('Admin');
      case 'tenant':   return profile.roles.includes('Tenant') || profile.roles.includes('Resident');
      default:         return false;
    }
  }, [profile]);

  // ── Actions ───────────────────────────────────────────────────────────────────
  const setActiveRole = useCallback(async (role: UserRole) => {
    if (!user || !profile?.roles.includes(role)) return;
    const { error } = await supabase.from('profiles').update({ active_role: role }).eq('id', user.id);
    if (error) throw error;
    setProfile(prev => prev ? { ...prev, active_role: role } : null);
  }, [user, profile]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email, password, options: { data: { full_name: fullName } },
    });
    if (error) throw error;
    if (data.user) {
      const { data: existing } = await supabase.from('profiles').select('id').eq('id', data.user.id).maybeSingle();
      if (!existing) {
        await supabase.from('profiles').insert([{
          id: data.user.id, full_name: fullName,
          roles: ['Guest'], active_role: 'Guest', onboarding_completed: false,
        }]);
      }
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("Supabase sign out error:", e);
    } finally {
      setSession(null);
      setUser(null);
      setProfile(null);
      sessionStorage.removeItem('majengo_has_booted');
      // Use window.location for a hard redirect — works regardless of
      // whether the React router has mounted or navigateRef is populated.
      window.location.href = '/login';
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const { data: { session: s }, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    setSession(s);
    setUser(s?.user ?? null);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const p = await fetchProfileFromDB(user.id, user);
    setProfile(p);
  }, [user]);

  const value = useMemo(() => ({
    session, user, profile, loading,
    hasRole, hasAnyRole, hasAllRoles,
    isAuthenticated: !!user,
    activeRole: profile?.active_role || null,
    setActiveRole, signIn, signUp, signOut,
    refreshSession, refreshProfile, canAccessPortal,
    // expose setNavigate so the router wrapper can inject it
    _setNavigate: setNavigate,
  } as any), [
    session, user, profile, loading,
    hasRole, hasAnyRole, hasAllRoles,
    setActiveRole, signIn, signUp, signOut,
    refreshSession, refreshProfile, canAccessPortal, setNavigate,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
